/**
 * @param {boolean} b
 * @return {string}
 */
const bool2str = b => b ? 'true' : 'false';

const setAttributes = (el, attr) =>
    Object.keys(attr).forEach(k => {
        const v = attr[k];
        if (v === null)
            el.removeAttribute(k);
        else
            el.setAttribute(k, v);
    });

/**
 * Replaces an anchor element with a button while keeping all relevant attributes
 * @param {HTMLAnchorElement|HTMLButtonElement} el
 * @param {boolean} ariaExpand
 * @param {boolean} hasPopup
 * @return {HTMLButtonElement}
 */
const linkToButton = (el, ariaExpand = true, hasPopup = false) => {
    const button = document.createElement('BUTTON');
    button.innerHTML = el.innerHTML;
    for (let i = 0; i < el.attributes.length; i++) {
        const attrib = el.attributes[i];
        if (attrib.name === 'href' || attrib.name === 'target') {
            continue;
        }
        button.setAttribute(attrib.name, attrib.value);
        if (ariaExpand) {
            button.setAttribute('aria-expanded', 'false');
        }
        if (hasPopup) {
            button.setAttribute('aria-haspopup', 'true');
        }
    }
    el.parentNode.insertBefore(button, el);
    el.parentNode.removeChild(el);
    return button;
};

/**
 * Compared to toggleAttribute it toggles the 'true'|'false' value
 * @param {{target: Element}} ev
 * @param {boolean} state
 */
const toggleAriaExpanded = (ev, state = undefined) => {
    ((el) => el.setAttribute('aria-expanded',
        state !== undefined
            ? state
            : bool2str(el.getAttribute('aria-expanded') !== 'true'))
    )(ev.target);
};

const KeyCode = {
    CANCEL: 3,
    HELP: 6,
    BACK_SPACE: 8,
    TAB: 9,
    CLEAR: 12,
    RETURN: 13,
    ENTER: 14,
    SHIFT: 16,
    CONTROL: 17,
    ALT: 18,
    PAUSE: 19,
    CAPS_LOCK: 20,
    ESCAPE: 27,
    SPACE: 32,
    PAGE_UP: 33,
    PAGE_DOWN: 34,
    END: 35,
    HOME: 36,
    LEFT: 37,
    UP: 38,
    RIGHT: 39,
    DOWN: 40,
    PRINTSCREEN: 44,
    INSERT: 45,
    DELETE: 46,
};

/**
 * Generate a short most likely document-wide unique id
 * @return {string}
 */
const componentId = () => Math.random().toString(32).slice(2, 12);

/**
 * @param {Element|Document} el
 * @return {(selector: string, (element: Element) => void) => void}
 */
export const forAllIn = (el) => (selector, fn) => Array.prototype.forEach.call(el.querySelectorAll(selector), fn);

/**
 * Applies a function to all matching document elements
 * @param {string} selector
 * @param {(element: Element) => void} fn
 */
const forAll = forAllIn(document);

/**
 * @template A
 * @param {(...args: A) => void} fn function to debounce
 * @param {number} delay ms
 * @returns {(...args: A) => void}
 */
const debounce = (fn, delay = 50) => {
    let to;
    return function() {
        if (to) {
            clearTimeout(to);
        }
        to = setTimeout(() => fn(arguments), delay);
    };
};

/**
 * Tries multiple times until the predicate is fulfilled to then execute te given callback
 * @param {(...args: any) => boolean} predicate
 * @returns {(callback: () => void, retries?: number, timeout?: number) => void}
 */
const awaitLoaded = predicate => (cb, retries = 2, timeout = 100) => {
    let _retries = retries;
    const initLate = () => {
        if (predicate()) {
            cb();
        } else {
            _retries--;
            if (_retries > 0) {
                setTimeout(initLate, timeout);
            }
        }
    };
    initLate();
};

/**
 * @param {string} selector
 * @returns {(callback: () => void, retries?: number, timeout?: number) => void}
 */
const awaitElement = selector => awaitLoaded(() => document.querySelector(selector));

/**
 * Helper function for appending child nodes
 * @param {HTMLElement} parentNode
 * @param {Node} child
 * @returns {void|*|ActiveX.IXMLDOMNode}
 */
const appendChild = (parentNode, child) => parentNode.appendChild(child);

/**
 * Migrates all child nodes within a parent node to a new parent
 * @param {HTMLElement} currentParent Parent node to take children from
 * @param {HTMLElement} newParent
 * @param {(element: HTMLElement, node: Node) => void} onInsert Option to overwrite how elements are inserted, so you could provide a function inserting before or after a pivot
 */
export const migrateChildNodes = (currentParent, newParent, onInsert = appendChild) => {
    while (currentParent.childNodes.length > 0) {
        onInsert(newParent, currentParent.childNodes[0]);
    }
};

/**
 * handle page teaser click
 * @param {Event} ev
 * @param {string} selector
 */
const handleLinkWrapperClick = (ev, selector) => {
    if (ev.target.closest('.neos-backend')) {
        return;
    }

    const path = ev.target.closest(selector).querySelector('a').getAttribute('href');
    const host = window.location.origin;
    (path.startsWith('http'))
        ? window.location.href = `${path}`
        : window.location.href = `${host}${path}`;
};

export {
    awaitElement,
    awaitLoaded,
    bool2str,
    componentId,
    debounce,
    forAll,
    handleLinkWrapperClick,
    setAttributes,
    KeyCode,
    linkToButton,
    toggleAriaExpanded,
};
